import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Clock, Check, Shield, Zap, Globe } from 'lucide-react';
import { getPlanPricing } from '../../lib/pricing';

const Step4Payment = ({ formData, loading, onNext, onPrevious, countryCode }) => {
  const { t } = useTranslation();
  const [paymentOption, setPaymentOption] = useState('trial');

  const selectedPlan = formData.selected_plan;
  const billingCycle = formData.billing_cycle || 'monthly';
  // Localized price (EUR for France, QAR for Qatar, DT otherwise) — same source
  // as the plan cards, and the amount/symbol match what the backend charges.
  const pricing = getPlanPricing(formData.plan_code, countryCode);
  const planAmount = billingCycle === 'annual' ? pricing.annual : pricing.monthly;
  const cycleWord = billingCycle === 'annual' ? t('registration.step3.year') : t('registration.step3.month');
  const priceLabel = `${planAmount ?? '—'} ${pricing.symbol} / ${cycleWord}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (paymentOption === 'trial') onNext({ skip_payment: true, payment_method: '', payment_gateway: '' });
    else onNext({ skip_payment: false, payment_method: 'stripe', payment_gateway: 'stripe' });
  };

  const submitLabel = { trial: t('registration.step4.startTrial'), stripe: t('registration.step4.continueToStripe') }[paymentOption];

  const OptionCard = ({ id, label, children }) => (
    <div
      onClick={() => setPaymentOption(id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setPaymentOption(id);
        }
      }}
      role="radio"
      tabIndex={0}
      aria-checked={paymentOption === id}
      aria-label={label}
      className="p-5 rounded-xl cursor-pointer transition-all"
      style={{
        backgroundColor: paymentOption === id ? "rgba(94,106,210,0.08)" : "var(--lm-s2)",
        border: `1px solid ${paymentOption === id ? "#5e6ad2" : "var(--lm-border)"}`,
      }}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0"
          style={{ border: `2px solid ${paymentOption === id ? "#5e6ad2" : "var(--lm-border-2)"}`, backgroundColor: paymentOption === id ? "#5e6ad2" : "transparent" }}>
          {paymentOption === id && <Check className="w-3 h-3 text-white" />}
        </div>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="p-8">
      <div className="mb-7">
        <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>{t('registration.step4.title')}</h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
          {paymentOption === 'trial' ? t('registration.step4.trialSubtitle') : t('registration.step4.paymentSubtitle')}
        </p>
      </div>

      {/* Plan summary */}
      {selectedPlan && (
        <div className="mb-6 p-5 rounded-xl" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: "var(--lm-ink-3)" }}>{t('registration.step4.selectedPlan')}</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: t('registration.step4.plan'), value: selectedPlan.plan_name, accent: true },
              { label: t('registration.step4.pricing'), value: priceLabel },
              { label: t('registration.step4.employees'), value: selectedPlan.employee_limit ? t('registration.step4.upTo', { count: selectedPlan.employee_limit }) : t('registration.step4.unlimited') },
              { label: t('registration.step4.storage'), value: `${selectedPlan.storage_limit_gb} GB` },
            ].map((item, i) => (
              <div key={i}>
                <p className="text-xs mb-0.5" style={{ color: "var(--lm-ink-3)" }}>{item.label}</p>
                <p className="text-sm font-medium" style={{ color: item.accent ? "#828fff" : "var(--lm-ink)" }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-3 mb-6" role="radiogroup" aria-label={t('registration.step4.paymentChoice')}>
          {/* Trial */}
          <OptionCard id="trial" label={t('registration.step4.startFreeTrial')}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Clock className="w-4 h-4" style={{ color: "#5e6ad2" }} />
              <span className="font-medium text-sm" style={{ color: "var(--lm-ink)" }}>{t('registration.step4.startFreeTrial')}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(39,166,68,0.15)", color: "#27a644" }}>{t('registration.step3.recommended')}</span>
            </div>
            <ul className="space-y-1">
              {[t('registration.step4.freeTrial14Days'), t('registration.step4.noCreditCard'), t('registration.step4.fullAccess'), t('registration.step4.cancelAnytime')].map(item => (
                <li key={item} className="flex items-center gap-2 text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  <Check className="w-3 h-3 flex-shrink-0" style={{ color: "#27a644" }} />{item}
                </li>
              ))}
            </ul>
          </OptionCard>

          {/* Stripe */}
          <OptionCard id="stripe" label={t('registration.step4.payImmediatelyStripe')}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Globe className="w-4 h-4" style={{ color: "#5e6ad2" }} />
              <span className="font-medium text-sm" style={{ color: "var(--lm-ink)" }}>{t('registration.step4.payImmediatelyStripe')}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)", border: "1px solid #23252a" }}>{t('registration.step4.international')}</span>
              <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: "rgba(94,106,210,0.1)", color: "#828fff" }}><Zap className="w-2.5 h-2.5" />{t('registration.step4.instantActivation')}</span>
            </div>
            <div className="mb-2 px-3 py-2 rounded-lg" style={{ backgroundColor: "var(--lm-s3)", border: "1px solid #23252a" }}>
              <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('registration.step4.amountToPay')}</p>
              <p className="text-lg font-semibold" style={{ color: "var(--lm-ink)" }}>{priceLabel}</p>
            </div>
            <ul className="space-y-1">
              {[t('registration.step4.stripeMethods'), t('registration.step4.immediateAccess'), t('registration.step4.stripeSecure')].map(item => (
                <li key={item} className="flex items-center gap-2 text-xs" style={{ color: "var(--lm-ink-2)" }}>
                  <Check className="w-3 h-3 flex-shrink-0" style={{ color: "#27a644" }} />{item}
                </li>
              ))}
            </ul>
          </OptionCard>
        </div>

        {/* Trust + terms */}
        <div className="flex items-center justify-center gap-6 flex-wrap mb-4 py-3 rounded-lg" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid #23252a" }}>
          {[{ icon: Shield, label: t('registration.step4.sslSecure') }, { icon: Check, label: t('registration.step4.encryptedData') }, { icon: Clock, label: t('registration.step4.freeCancellation') }].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-xs" style={{ color: "var(--lm-ink-3)" }}>
              <Icon className="w-3.5 h-3.5" style={{ color: "#27a644" }} />{label}
            </div>
          ))}
        </div>

        <p className="text-xs mb-6" style={{ color: "var(--lm-ink-3)" }}>
          {paymentOption === 'trial' ? t('registration.step4.termsNoticeTrial') : t('registration.step4.termsNoticePayment')}{' '}
          <a href="/terms" className="underline" style={{ color: "#5e6ad2" }} target="_blank" rel="noopener noreferrer">{t('registration.step4.termsOfService')}</a>
          {' '}{t('registration.step4.and')}{' '}
          <a href="/privacy" className="underline" style={{ color: "#5e6ad2" }} target="_blank" rel="noopener noreferrer">{t('registration.step4.privacyPolicy')}</a>.
        </p>

        <div className="flex justify-between pt-5" style={{ borderTop: "1px solid #23252a" }}>
          <button type="button" onClick={onPrevious} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink)", border: "1px solid #23252a" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--lm-s3)")} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}>
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            {t('common.back')}
          </button>
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}>
            {loading ? t('common.processing') : submitLabel}
            {!loading && <ArrowRight className="w-4 h-4 rtl:rotate-180" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step4Payment;
